import {Inject, Injectable, Optional, PLATFORM_ID} from '@angular/core';
import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {REQUEST} from '@nguniversal/express-engine/tokens';
import {Request} from 'express';

@Injectable({
    providedIn: 'root'
})
export class CookiesStorageService {

    private readonly _isDocumentAccessible: boolean;

    constructor(@Inject(DOCUMENT) private _document: Document,
                @Inject(PLATFORM_ID) private _platformId: object,
                @Optional() @Inject(REQUEST) private _request: Request) {
        this._isDocumentAccessible = isPlatformBrowser(this._platformId);
    }

    get cookieString() {
        if (!this._isDocumentAccessible) {
            return (this._request && this._request.headers.cookie && this._request.headers.cookie) || '';
        }

        return (this._document && this._document.cookie) || '';
    }

    set cookieString(newValue: string) {
        if (!this._isDocumentAccessible) {
            if (!this._request) {
                return;
            }

            this._request.cookies = newValue;
            return;
        }

        if (!this._document) {
            return;
        }

        this._document.cookie = newValue;
    }

    get<T = any>(token: string) {
        // Get all values and pick one out.
        const values = this.all();
        return values[token] as T;
    }

    set<T = any>(token: string, value: T, attributes?: CookieStorageAttributes) {
        // Write a new cookie.
        this.cookieString = this.encode(token, value, {path: '/', ...attributes});
    }

    remove(token: string, attributes?: CookieStorageAttributes) {
        this.set(token, '', {...attributes, expires: -1});
    }

    clear(attributes?: CookieStorageAttributes) {
        const items = this.all();

        Object.keys(items)
            .forEach(token => this.remove(token, attributes));
    }

    private all() {
        return this.decode(this.cookieString);
    }

    private encode(token: string, value: any, attributes: CookieStorageAttributes) {
        // Encode the token.
        const encodedToken = encodeURIComponent(token)
            .replace(/%(23|24|26|2B|5E|60|7C)/g, decodeURIComponent)
            .replace(/\(/g, '%28').replace(/\)/g, '%29');

        const rawValue = JSON.stringify(value);

        // Encode the value.
        const encodedValue = encodeURIComponent(rawValue)
            .replace(/%(23|24|26|2B|3A|3C|3E|3D|2F|3F|40|5B|5D|5E|60|7B|7D|7C)/g, decodeURIComponent);

        // Return the completed encoded string.
        return [encodedToken, '=', encodedValue, this.stringifyCookie(attributes)].join('');
    }

    private decode = (cookieString: string) => {
        const result: { [token: string]: any } = {};
        const cookies = cookieString ? cookieString.split('; ') : [];
        const rdecode = /(%[0-9A-Z]{2})+/g;

        for (let index = 0; index < cookies.length; index++) {
            const parts = cookies[index].split('=');
            let cookie = parts.slice(1).join('=');

            if (cookie.charAt(0) === '"') {
                cookie = cookie.slice(1, -1);
            }

            try {
                const name = parts[0].replace(rdecode, decodeURIComponent);
                const value = cookie.replace(rdecode, decodeURIComponent);
                result[name] = this.jsonParse(value);
            } catch {
                // Ignore cookies with an invalid name/value encoding.
            }
        }

        return result;
    };

    private jsonParse = <T = any>(rawValue: string) => {
        // Try to parse the data.
        let parsedValue: T | undefined = undefined;

        // Only parse the value when it is not null.
        if (rawValue !== undefined) {
            try {
                parsedValue = JSON.parse(rawValue);
            } catch {
                // The string could not be parsed to JSON.
            }
        }

        // Return the parsed value.
        return parsedValue;
    };

    private stringifyCookie(attributes: CookieStorageAttributes) {
        // Make sure the expires attribute is a date and not a number.
        if (typeof attributes.expires === 'number') {
            const expires = new Date();
            expires.setMilliseconds(expires.getMilliseconds() + attributes.expires * 864e+5);
            attributes.expires = expires;
        }

        // Stringify all attributes.
        return this.stringifyAttribute('Expires', attributes.expires ? attributes.expires.toUTCString() : '')
            + this.stringifyAttribute('Domain', attributes.domain)
            + this.stringifyAttribute('Path', attributes.path)
            + this.stringifyAttribute('Secure', attributes.secure)
            + this.stringifyAttribute('SameSite', attributes.sameSite);
    }

    private stringifyAttribute = (token: string, value: string | boolean | undefined) => {
        if (!value) {
            return '';
        }

        const stringified = `; ${token}`;

        // Boolean attributes should not have a value.
        if (value === true) {
            return stringified;
        }

        return stringified + `= ${value}`;
    };

}

export interface CookieStorageAttributes {
    expires?: Date | number;
    domain?: string;
    path?: string;
    secure?: boolean;
    sameSite?: 'strict' | 'lax';
}
